import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { API_URL } from '../api/config'

/**
 * Os números que a organização pede: público por dia, credenciamento por dia e de onde
 * vem a gente.
 *
 * Cada bloco separa PESSOAS de LEITURAS. Sem controle de saída a mesma pessoa lê o crachá
 * várias vezes no dia, e reimpressão de etiqueta grava check-in novo — somar os registros
 * responderia "quantas passagens houve", que é um número bem maior e outra pergunta.
 */

interface DiaAcesso {
  dia: string
  pessoas: number
  leituras: number
  salas: number
}

interface DiaCheckin {
  dia: string
  pessoas: number
  retiradas: number
}

interface Origem {
  estados: Array<{ uf: string; nome: string; total: number }>
  nacionalidades: Array<{ valor: string; total: number }>
  semEstado: number
  totalPessoas: number
}

const diaLegivel = (ymd: string) => {
  const [a, m, d] = ymd.split('-')
  if (!a || !m || !d) return ymd
  const data = new Date(Number(a), Number(m) - 1, Number(d))
  const semana = data.toLocaleDateString('pt-BR', { weekday: 'short' })
  return `${d}/${m} · ${semana.replace('.', '')}`
}

const Painel: React.FC<{
  titulo: string
  descricao: string
  carregando: boolean
  children: React.ReactNode
}> = ({ titulo, descricao, carregando, children }) => (
  <Box
    borderWidth="1px"
    borderColor={useColorModeValue('gray.200', 'gray.700')}
    borderRadius="lg"
    bg={useColorModeValue('white', 'gray.800')}
    p={5}
  >
    <Heading size="sm" mb={1}>
      {titulo}
    </Heading>
    <Text fontSize="xs" color="gray.500" mb={4}>
      {descricao}
    </Text>
    {carregando ? <Skeleton height="120px" borderRadius="md" /> : children}
  </Box>
)

const ResumoDoEvento: React.FC = () => {
  const [acessos, setAcessos] = useState<DiaAcesso[]>([])
  const [checkins, setCheckins] = useState<DiaCheckin[]>([])
  const [origem, setOrigem] = useState<Origem | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    Promise.all([
      axios.get(`${API_URL}access/por-dia`),
      axios.get(`${API_URL}checkin/por-dia`),
      axios.get(`${API_URL}users/por-origem`),
    ])
      .then(([a, c, o]) => {
        if (!vivo) return
        setAcessos(a.data || [])
        setCheckins(c.data || [])
        setOrigem(o.data || null)
      })
      .catch(e => {
        if (vivo) setErro(e?.message || 'Não foi possível carregar os números.')
      })
      .finally(() => {
        if (vivo) setCarregando(false)
      })
    return () => {
      vivo = false
    }
  }, [])

  const totalAcessos = acessos.reduce((s, d) => s + d.pessoas, 0)
  const totalCheckins = checkins.reduce((s, d) => s + d.pessoas, 0)

  return (
    <Box mb={8}>
      <Heading size="lg" mb={1}>
        Resumo do evento
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={5}>
        Números consolidados para a organização.
      </Text>

      {erro && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {erro}
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5}>
        <Painel
          titulo="Público nas palestras, por dia"
          descricao="Pessoas distintas que entraram em alguma sala"
          carregando={carregando}
        >
          {acessos.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              Nenhum acesso registrado.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Dia</Th>
                  <Th isNumeric>Pessoas</Th>
                  <Th isNumeric>Leituras</Th>
                  <Th isNumeric>Salas</Th>
                </Tr>
              </Thead>
              <Tbody>
                {acessos.map(d => (
                  <Tr key={d.dia}>
                    <Td>{diaLegivel(d.dia)}</Td>
                    <Td isNumeric fontWeight="bold">
                      {d.pessoas}
                    </Td>
                    <Td isNumeric color="gray.500">
                      {d.leituras}
                    </Td>
                    <Td isNumeric color="gray.500">
                      {d.salas}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
          {/*
            A soma dos dias NÃO é o público do evento: quem foi nos três dias aparece nos
            três. Dizer isso evita que o número vire "3× participantes" num relatório.
          */}
          {acessos.length > 1 && (
            <Text fontSize="xs" color="gray.500" mt={3}>
              Somando os dias: {totalAcessos}. Quem foi em mais de um dia conta em cada um.
            </Text>
          )}
        </Painel>

        <Painel
          titulo="Credenciamento (check-in), por dia"
          descricao="Pessoas que retiraram credencial no balcão ou no totem"
          carregando={carregando}
        >
          {checkins.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              Nenhum check-in registrado.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Dia</Th>
                  <Th isNumeric>Pessoas</Th>
                  <Th isNumeric>Retiradas</Th>
                </Tr>
              </Thead>
              <Tbody>
                {checkins.map(d => (
                  <Tr key={d.dia}>
                    <Td>{diaLegivel(d.dia)}</Td>
                    <Td isNumeric fontWeight="bold">
                      {d.pessoas}
                    </Td>
                    <Td isNumeric color="gray.500">
                      {d.retiradas}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
          {checkins.length > 1 && (
            <Text fontSize="xs" color="gray.500" mt={3}>
              Somando os dias: {totalCheckins}. "Retiradas" inclui reimpressão de etiqueta.
            </Text>
          )}
        </Painel>

        <Painel
          titulo="De onde vem o público"
          descricao="Estado declarado na inscrição"
          carregando={carregando}
        >
          {!origem ? null : (
            <>
              <Flex align="baseline" gap={2} mb={3}>
                <Text fontSize="3xl" fontWeight="800" lineHeight="1" color="teal.600">
                  {origem.estados.length}
                </Text>
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  {origem.estados.length === 1 ? 'estado' : 'estados'}
                </Text>
              </Flex>

              <Box maxH="220px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Tbody>
                    {origem.estados.map(e => (
                      <Tr key={e.uf}>
                        <Td py={1}>
                          <Badge colorScheme="teal" variant="subtle" mr={2}>
                            {e.uf}
                          </Badge>
                          <Text as="span" fontSize="sm" color="gray.600">
                            {e.nome}
                          </Text>
                        </Td>
                        <Td py={1} isNumeric fontWeight="600">
                          {e.total}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {origem.semEstado > 0 && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {origem.semEstado} pessoa(s) sem estado informado.
                </Text>
              )}

              {/*
                País não é campo da inscrição. Dizer o que existe — a nacionalidade
                declarada — é mais útil que devolver um número inventado a partir de
                domínio de e-mail ou DDD.
              */}
              <Box mt={4} pt={3} borderTopWidth="1px" borderColor="gray.200">
                <Text fontSize="xs" fontWeight="700" color="gray.600" mb={1}>
                  NACIONALIDADE
                </Text>
                {origem.nacionalidades.map(n => (
                  <Flex key={n.valor} justify="space-between" fontSize="sm">
                    <Text color="gray.600" textTransform="capitalize">
                      {n.valor}
                    </Text>
                    <Text fontWeight="600">{n.total}</Text>
                  </Flex>
                ))}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  O país de origem não é perguntado na inscrição — só se a pessoa é
                  brasileira ou estrangeira.
                </Text>
              </Box>
            </>
          )}
        </Painel>
      </SimpleGrid>
    </Box>
  )
}

export default ResumoDoEvento
